import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import { showsPrice, type QuoteCard } from "@/lib/submissions/quoteCards";

// BRK-S509 — Signal's own PDF of a quote or an indication (PMS-S932), for the
// broker to share with the insured. Great American sends no quote PDF, so PMS
// renders one, keeps its revisions, and serves the current one from a single
// broker route (`getQuoteDocument`). Tyler's demo, 10/29.

/** The quote popup's action, by the quote's kind. */
export const QUOTE_DOCUMENT_LABELS: Readonly<
  Record<BrokerQuote["kind"], string>
> = {
  quote: "Download Quote",
  indication: "Download Indication",
};

/** Whether PMS has a document for the quote: PMS-S932's `isQuotePriced`, not
 *  a draft and a total cost above 0, the itemized total first. Any other
 *  quote is a 409 `quote_not_priced`, so the portal never offers it. */
export function hasQuoteDocument(
  quote: Pick<BrokerQuote, "stage" | "totalCost" | "terms">,
): boolean {
  return (
    quote.stage !== "draft" &&
    Number(quote.terms?.totals.totalCost ?? quote.totalCost) > 0
  );
}

/** Whether a card offers the download: it shows a price (`showsPrice`), and
 *  PMS has a document for that price. */
export function offersQuoteDocument(card: QuoteCard): boolean {
  return showsPrice(card) && hasQuoteDocument(card.quote);
}

/** The name to save under when PMS sends no `Content-Disposition`: PMS's own
 *  pattern (`QTE-548NK-00010-quote.pdf`), else the kind. Never the id
 *  (`saveFile`). */
export function quoteDocumentFallback(
  quote: Pick<BrokerQuote, "kind" | "quoteNumber">,
): string {
  return quote.quoteNumber
    ? `${quote.quoteNumber}-${quote.kind}.pdf`
    : `${quote.kind}.pdf`;
}
